import { useEffect, useRef } from 'react'
import useBodyScrollLock from './useBodyScrollLock'
import useExitTransition from './useExitTransition'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Everything a modal surface has to DO, in one place.
 *
 * The kit had four dialog shells and four different answers to the same five
 * questions. Only the side sheet trapped Tab — in the confirmation, the outcome
 * and the learner's briefing you could tab straight out of an "aria-modal"
 * dialog into the page behind it and press the button the dialog was asking
 * about. Only the side sheet gave focus back to the control that opened it;
 * the other three dropped the caret on <body>, so a keyboard reader who closed
 * a dialog restarted from the top of the page. Two of the four animated out and
 * two were torn off the frame the button was pressed.
 *
 * None of that is a per-dialog decision. It is what "modal" means, so it lives
 * here and every surface gets the same answer:
 *
 *   - the page is locked and the scroll position kept
 *   - Escape closes
 *   - Tab cycles INSIDE the panel, both directions
 *   - focus starts somewhere deliberate and returns to the opener afterwards
 *   - the panel stays mounted long enough to animate out
 *
 * Composed from the hooks that already existed rather than reimplementing them:
 * useBodyScrollLock is ref-counted (dialogs do stack), and useExitTransition
 * ends the wait on the box's own animationend so durations stay in the
 * stylesheet with the rest of the motion.
 *
 * @param {object}  o
 * @param {boolean} o.open
 * @param {() => void} o.onClose          Escape and the scrim route through this
 * @param {React.RefObject} [o.initialFocus]  focused on open; the panel if omitted
 * @param {boolean} [o.trap]              keep Tab inside the panel
 * @returns {{mounted: boolean, state: 'entering'|'leaving', ref: React.RefObject}}
 *   `ref` goes on the panel — the element that both carries the exit animation
 *   and bounds the focus trap. In all four shells those are the same node.
 */
export default function useDialog({ open, onClose, initialFocus, trap = true }) {
  /* onClose is nearly always an inline arrow, so it must not sit in an effect's
     deps — re-running the focus effect on every parent render would pull the
     caret out of whatever field is being typed in. */
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const { mounted, state, ref } = useExitTransition(open)

  useBodyScrollLock(open)

  /* The control that opened the dialog, so it can be handed focus back. Captured
     on the way in rather than read on the way out: by then the dialog's own
     button is the active element. */
  const opener = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    opener.current = document.activeElement

    /* On document, not on the panel: clicking the scrim moves focus to <body>,
       and Escape has to keep working from there. */
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        /* A surface with NO onClose has no way out by design — the portal gate, which
           interrupts rather than being opened and has nothing to cancel. Escape must then do
           nothing at all, rather than be swallowed here on its way to something else. */
        if (onCloseRef.current) {
          event.stopPropagation()
          onCloseRef.current()
        }
        return
      }
      if (event.key !== 'Tab' || !trap) return

      const items = [...(ref.current?.querySelectorAll(FOCUSABLE) ?? [])].filter(
        /* offsetParent skips anything display:none — a collapsed section's
           fields are in the DOM but are not stops on the way round. */
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (items.length === 0) {
        /* Nothing focusable: Tab must still not escape to the page behind. */
        event.preventDefault()
        ref.current?.focus({ preventScroll: true })
        return
      }

      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      const inside = ref.current?.contains(active)

      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
    /* ref is a stable useRef box; initialFocus likewise. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trap])

  /* Focused programmatically, NOT with the autoFocus attribute. The attribute
     counts as a non-pointer interaction, so the button paints :focus-visible
     the instant the dialog opens and every dialog arrived wearing a ring. A
     plain .focus() follows the browser's own modality heuristic instead:
     keyboard users still get the ring, a click does not summon one.

     ONCE THE PANEL EXISTS. `mounted` turns true a render after `open`, so this ran
     against an empty ref when it lived in the effect above, and focus stayed on the
     page behind a drawer until the first Tab pulled it in.

     preventScroll because the panel is portalled to <body> while the page
     behind it is still scrolled where the reader left it. */
  useEffect(() => {
    if (!open || !mounted) return
    const target = initialFocus?.current ?? ref.current
    target?.focus({ preventScroll: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mounted])

  /* Handed back as the dialog STARTS to leave, not once it has gone: the exiting
     panel is marked inert, and focus must not be sitting inside an inert subtree
     while it animates. */
  useEffect(() => {
    if (open) return undefined
    const el = opener.current
    opener.current = null
    if (el instanceof HTMLElement && el.isConnected) el.focus({ preventScroll: true })
    return undefined
  }, [open])

  return { mounted, state, ref }
}
